import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CTX = createContext(null);
const BEARER_KEY = "darkside_customer_token"; // Fallback storage for browsers that block third-party cookies

/**
 * Customer auth context. Wraps the app and exposes:
 *  - customer: the logged-in customer profile (or null)
 *  - loading: initial /me fetch in progress
 *  - refresh(): re-fetch /me
 *  - login(): kicks off Emergent Google Auth OAuth flow
 *  - logout(): clears session + local state
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => {
    const t = typeof window !== "undefined" ? localStorage.getItem(BEARER_KEY) : null;
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customer/auth/me`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setCustomer(res.data);
      return res.data;
    } catch (_) {
      setCustomer(null);
      return null;
    }
  }, [authHeaders]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/customer/auth/logout`, {}, {
        withCredentials: true,
        headers: authHeaders(),
      });
    } catch (_) { /* ignore */ }
    try { localStorage.removeItem(BEARER_KEY); } catch (_) { /* ignore */ }
    setCustomer(null);
  }, [authHeaders]);

  const value = useMemo(() => ({
    customer,
    loading,
    login,
    logout,
    refresh,
    authHeaders,
  }), [customer, loading, login, logout, refresh, authHeaders]);

  return <CTX.Provider value={value}>{children}</CTX.Provider>;
}

export function useCustomerAuth() {
  const ctx = useContext(CTX);
  if (!ctx) throw new Error("useCustomerAuth must be used inside CustomerAuthProvider");
  return ctx;
}

// Helper for one-off token save (used by AuthCallback)
export function saveBearerToken(token) {
  try { localStorage.setItem(BEARER_KEY, token); } catch (_) { /* ignore */ }
}
